import { useEffect, useState } from 'react';
import { readCookie, setCookieIfValueExists } from 'src/utils/Cookies';
import { AGENT_ID_COOKIE_NAME, SF_PARTNER_ID_COOKIE_NAME, addUserPropertiesToAnalytics, getUrlAgentIdFromLocalStorage } from './analytics';
import { getDefaultOrResellerChannelInfo } from './channelInfo';

export type PartnerAttributionContext = ReturnType<typeof usePartnerAttributionInternal>;
export type AttributeSource = 'URL' | 'SPEEDBUMP' | 'DEFAULT';

const defaultAttributes = {
  URL: undefined as string | undefined,
  SPEEDBUMP: undefined as string | undefined,
  DEFAULT: undefined as string | undefined,
};

export const usePartnerAttributionInternal = () => {
  // `referralId`, `reseller_group`, `reseller_service` also used for attribution

  const [ resellerId, setResellerId ] = useState(undefined as string | undefined);
  const [ verifiedAgentId, setVerifiedAgentId] = useState(defaultAttributes);
  const [ unverifiedAgentId, setUnverifiedAgentId ] = useState({
    ...defaultAttributes,
    DEFAULT: readCookie(AGENT_ID_COOKIE_NAME) || undefined,
  });

  const updateUnverifiedAgentId = (newAgentId: string, source: AttributeSource) => {
    if (newAgentId === unverifiedAgentId[source]) return;
    setUnverifiedAgentId({ ...unverifiedAgentId, [source]: newAgentId });
  };

  const getUnverifiedAgentId = () => unverifiedAgentId.URL || unverifiedAgentId.SPEEDBUMP || unverifiedAgentId.DEFAULT;

  const getAgentId = () => {
    return verifiedAgentId.URL
    || verifiedAgentId.SPEEDBUMP
    || verifiedAgentId.DEFAULT
    || getUnverifiedAgentId();
  };

  const getPriorityField = (field: typeof defaultAttributes) => {
    if (field.URL) return 'URL';
    if (field.SPEEDBUMP) return 'SPEEDBUMP';
    return 'DEFAULT';
  };

  useEffect(() => {
    const urlAgentId = getUrlAgentIdFromLocalStorage();
    if (urlAgentId && urlAgentId !== unverifiedAgentId.URL) {
      updateUnverifiedAgentId(urlAgentId, 'URL');
      return;
    }
    const bestAgentId = getUnverifiedAgentId();

    getDefaultOrResellerChannelInfo(resellerId, bestAgentId)
    .then(result => {
      if (result) {
        const agentIdPriorityField = getPriorityField(unverifiedAgentId);
        setVerifiedAgentId({ ...verifiedAgentId, [agentIdPriorityField]: result.chainAgent });

        setCookieIfValueExists(SF_PARTNER_ID_COOKIE_NAME, result.salesforcePartnerId, 365 * 2);
        addUserPropertiesToAnalytics({ salesforcePartnerId: result.salesforcePartnerId });
      } else {
        if (readCookie(AGENT_ID_COOKIE_NAME) === bestAgentId) return;
        setCookieIfValueExists(AGENT_ID_COOKIE_NAME, bestAgentId, 365 * 2);
        addUserPropertiesToAnalytics({ agentId: bestAgentId });
      }
    });

  }, [unverifiedAgentId, resellerId]);

  useEffect(() => {
    if (readCookie(AGENT_ID_COOKIE_NAME) === getAgentId()) return;
    setCookieIfValueExists(AGENT_ID_COOKIE_NAME, getAgentId(), 365 * 2);
    addUserPropertiesToAnalytics({ agentId: getAgentId() });
  }, [verifiedAgentId]);

  return {
    setResellerId,
    getAgentId,
  };
};
